import React, { useState, useEffect } from 'react';
import './AdminLive.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://ceindiazone2.in';
const ADMIN_SECRET = 'cepune-admin-2026';

const AdminLive = () => {
  const [videoId, setVideoId] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' | 'error'
  const [isLoading, setIsLoading] = useState(false);
  const [currentState, setCurrentState] = useState(null);

  const fetchCurrentState = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/youtube/admin-status?secret=${ADMIN_SECRET}`);
      const data = await res.json();
      if (data.success !== false) {
        setCurrentState(data);
      }
    } catch {
      setCurrentState(null);
    }
  };

  useEffect(() => {
    fetchCurrentState();
  }, []);

  const handleGoLive = async () => {
    if (!videoId.trim()) {
      setMessageType('error');
      setStatusMessage('Please enter a valid YouTube Video ID.');
      return;
    }
    setIsLoading(true);
    setStatusMessage('');
    try {
      const res = await fetch(
        `${API_BASE}/api/youtube/force-live?videoId=${encodeURIComponent(videoId.trim())}&secret=${ADMIN_SECRET}`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (data.success) {
        setMessageType('success');
        setStatusMessage(`✅ Live stream activated! Video ID: ${data.videoId}`);
        setVideoId('');
        await fetchCurrentState();
      } else {
        setMessageType('error');
        setStatusMessage(`❌ ${data.message}`);
      }
    } catch {
      setMessageType('error');
      setStatusMessage('❌ Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndLive = async () => {
    setIsLoading(true);
    setStatusMessage('');
    try {
      const res = await fetch(
        `${API_BASE}/api/youtube/clear-live?secret=${ADMIN_SECRET}`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (data.success) {
        setMessageType('success');
        setStatusMessage('✅ Live override cleared. Site will auto-detect YouTube status.');
        await fetchCurrentState();
      } else {
        setMessageType('error');
        setStatusMessage(`❌ ${data.message}`);
      }
    } catch {
      setMessageType('error');
      setStatusMessage('❌ Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const isOverrideActive = currentState?.forceLiveActive;

  return (
    <div className="admin-live-page">
      <div className="admin-live-card">
        {/* Header */}
        <div className="admin-live-header">
          <div className="admin-live-logo">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </div>
          <div>
            <h1 className="admin-live-title">Live Stream Control</h1>
            <p className="admin-live-subtitle">Christ Embassy Pune — Admin Panel</p>
          </div>
        </div>

        {/* Current Status */}
        <div className={`status-banner ${isOverrideActive ? 'active' : 'inactive'}`}>
          <span className={`status-dot ${isOverrideActive ? 'blink' : ''}`}></span>
          <div>
            <strong>{isOverrideActive ? 'LIVE NOW (Override Active)' : 'Auto-Detection Mode'}</strong>
            {isOverrideActive && currentState?.currentStatus?.videoId && (
              <p className="status-video-id">
                Video ID: <code>{currentState.currentStatus.videoId}</code>
              </p>
            )}
            {!isOverrideActive && (
              <p className="status-video-id">The website is using the YouTube API to detect live streams automatically.</p>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="admin-live-divider"></div>

        {/* Go Live Section */}
        <div className="admin-live-section">
          <h2 className="section-heading">
            <span className="section-icon">📡</span>
            Activate Live Stream
          </h2>
          <p className="section-desc">
            When you go live on YouTube (including <strong>unlisted</strong> streams), paste the Video ID below.
            The LIVE button on the website will blink red and the video will play automatically.
          </p>
          <div className="input-row">
            <input
              id="videoIdInput"
              type="text"
              className="video-id-input"
              placeholder="e.g. dQw4w9WgXcQ"
              value={videoId}
              onChange={(e) => setVideoId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGoLive()}
              disabled={isLoading}
            />
            <button
              id="goLiveBtn"
              className="btn btn-live"
              onClick={handleGoLive}
              disabled={isLoading}
            >
              {isLoading ? 'Activating...' : '🔴 Go Live'}
            </button>
          </div>
          <p className="input-hint">
            💡 To find the Video ID: open the YouTube video → copy the part after <code>?v=</code> in the URL.
            <br />For example: youtube.com/watch?v=<strong>dQw4w9WgXcQ</strong> → Video ID is <strong>dQw4w9WgXcQ</strong>
          </p>
        </div>

        {/* Divider */}
        <div className="admin-live-divider"></div>

        {/* End Live Section */}
        <div className="admin-live-section">
          <h2 className="section-heading">
            <span className="section-icon">⏹️</span>
            End Live Stream
          </h2>
          <p className="section-desc">
            After your stream ends, click below to clear the override. The website will go back to automatically detecting live streams via the YouTube API.
          </p>
          <button
            id="endLiveBtn"
            className="btn btn-end"
            onClick={handleEndLive}
            disabled={isLoading || !isOverrideActive}
          >
            {isLoading ? 'Clearing...' : '⏹ End Live & Go Offline'}
          </button>
          {!isOverrideActive && (
            <p className="input-hint" style={{ marginTop: '8px' }}>No active override to clear.</p>
          )}
        </div>

        {/* Feedback Message */}
        {statusMessage && (
          <div className={`feedback-message ${messageType}`}>
            {statusMessage}
          </div>
        )}

        {/* Refresh */}
        <button className="btn btn-refresh" onClick={fetchCurrentState} disabled={isLoading}>
          🔄 Refresh Status
        </button>
      </div>
    </div>
  );
};

export default AdminLive;
